import clsx from "clsx";
import { useRouter } from "next/router";
import { FunctionComponent, useEffect, useState } from "react";

import {
  DevPanelCloseIcon,
  DevPanelMinimizeIcon,
  DevPanelPhoneIcon,
} from "src/components/dev/DevPanelIcons";
import { useCskDevLanOrigin } from "src/hooks/use-csk-dev-lan-origin";
import { buildCskDevLanUrl } from "src/utils/dev-lan-url";
import {
  DEV_DEVICE_QR_DISMISS_KEY,
  readDevPanelDismissed,
  writeDevPanelDismissed,
} from "src/utils/dev-panel-session";

import * as styles from "./DevDeviceQr.css";
import DevDeviceQrCode from "./DevDeviceQrCode";

/**
 * Development-only floating control for opening the current page on a LAN device.
 * LAN IP is auto-detected via /api/dev-lan-origin (no .env.local IP to maintain).
 */
const DevDeviceQr: FunctionComponent = () => {
  const router = useRouter();
  const { lanOrigin, loading } = useCskDevLanOrigin();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (readDevPanelDismissed(DEV_DEVICE_QR_DISMISS_KEY)) {
      setDismissed(true);
    }
  }, []);

  const lanUrl = lanOrigin ? buildCskDevLanUrl(lanOrigin, router.asPath) : null;

  if (dismissed) {
    return null;
  }

  const dismiss = () => {
    writeDevPanelDismissed(DEV_DEVICE_QR_DISMISS_KEY);
    setDismissed(true);
  };

  const copyLink = () => {
    if (!lanUrl) {
      return;
    }
    navigator.clipboard
      .writeText(lanUrl)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  return expanded ? (
    <aside className={styles.panel} role="status" aria-label="Open on device">
      <div className={styles.header}>
        <span className={styles.label}>Open on device</span>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => setExpanded(false)}
            aria-label="Minimize device QR"
          >
            <DevPanelMinimizeIcon />
          </button>
          <button
            type="button"
            className={styles.iconButton}
            onClick={dismiss}
            aria-label="Dismiss device QR"
          >
            <DevPanelCloseIcon />
          </button>
        </div>
      </div>
      <div className={styles.qrWrap}>
        {loading ? (
          <p className={styles.setupMessage}>Detecting LAN address…</p>
        ) : lanUrl ? (
          <DevDeviceQrCode value={lanUrl} />
        ) : (
          <p className={styles.setupMessage}>
            Could not detect a LAN IP. Connect to Wi‑Fi and restart the dev
            server, or set <code>NEXT_PUBLIC_CSK_DEV_LAN_ORIGIN</code> in{" "}
            <code>.env.local</code> as a fallback.
          </p>
        )}
      </div>
      {lanUrl ? (
        <>
          <p className={styles.url}>{lanUrl}</p>
          <button
            type="button"
            className={styles.copyButton}
            onClick={copyLink}
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </>
      ) : null}
    </aside>
  ) : (
    <button
      type="button"
      className={clsx(styles.fab)}
      onClick={() => setExpanded(true)}
      aria-label="Open device QR"
      aria-expanded={false}
    >
      <DevPanelPhoneIcon />
    </button>
  );
};

export default DevDeviceQr;
